// Frame for the signed-out routes (sign-in, MFA, passkey, invite accept): a
// brand panel with the dune horizon on wide screens, the form column beside
// it; stacked on phones. The horizon is the portals' one memorable element —
// it appears here large, and small at the foot of the sidebar once inside.
import type { ReactNode } from 'react';
import { BrandMark } from './Shell';

export function PublicFrame({ tone, children }: { tone: 'business' | 'admin'; children: ReactNode }) {
  const admin = tone === 'admin';
  return (
    <div className="min-h-full lg:grid lg:grid-cols-[minmax(320px,44%)_minmax(0,1fr)]">
      <section
        aria-hidden="true"
        className={`relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between ${admin ? 'bg-ink-bar text-white' : 'bg-tint-sand text-ink'}`}
      >
        <div className="flex items-center gap-md p-xxl">
          <BrandMark size={40} tone={tone} />
          <span className="leading-tight">
            <span className="block text-h3 font-extrabold tracking-tight">NamibiaGo</span>
            <span className={`block text-small font-semibold ${admin ? 'text-white/60' : 'text-ink-2'}`}>
              {admin ? 'Founder console' : 'Business portal'}
            </span>
          </span>
        </div>
        <div className="relative z-10 px-xxl pb-[38%]">
          <p className="max-w-[20ch] text-display font-extrabold leading-[1.05] tracking-tight">
            {admin ? 'Keep the platform honest.' : 'Your desk, from the dunes to the coast.'}
          </p>
          <p className={`mt-md max-w-[40ch] text-body ${admin ? 'text-white/70' : 'text-ink-2'}`}>
            {admin
              ? 'Approvals, money and webhook health in one place, every action logged with a reason.'
              : 'Bookings, check-ins, rates and statements for every property you run on NamibiaGo.'}
          </p>
        </div>
        <svg className="absolute inset-x-0 bottom-0 w-full" viewBox="0 0 600 300" preserveAspectRatio="none">
          <circle cx="440" cy="92" r="46" fill="var(--gold)" opacity={admin ? 0.95 : 0.9} />
          <path d="M0 170c90-60 180-70 270-30 80 36 160 34 330-30v190H0z" fill={admin ? 'rgba(255,255,255,0.06)' : 'var(--surface)'} opacity={admin ? 1 : 0.6} />
          <path d="M0 220c110-56 200-60 290-22 76 32 150 28 310-24v126H0z" fill={admin ? 'rgba(255,255,255,0.10)' : 'var(--sand)'} opacity={admin ? 1 : 0.35} />
          <path d="M0 262c120-40 220-40 310-14 70 20 140 18 290-16v68H0z" fill="var(--canyon)" opacity={admin ? 0.9 : 0.8} />
          <path d="M0 290c140-24 260-22 350-8 70 10 150 8 250-6v24H0z" fill="var(--desert)" />
        </svg>
      </section>

      <main className="flex min-h-full flex-col px-lg py-xl lg:px-xxl lg:py-xxl">
        <div className="mb-xl flex items-center gap-sm lg:hidden">
          <BrandMark size={28} tone={tone} />
          <span className="text-nav-title font-extrabold tracking-tight text-ink">
            NamibiaGo <span className="text-ink-2">{admin ? 'Admin' : 'Business'}</span>
          </span>
        </div>
        <div className="ng-reveal m-auto w-full max-w-[420px]">{children}</div>
      </main>
    </div>
  );
}
